/**
 * Utility functions for log inspector API endpoints
 */
import type { GetEventsInput } from '@squareup/dex-types-shared-api-webhook-logs';

// Map display values to boolean values for specific filters
const isTestEventOptionMap: Record<string, boolean> = {
  'Test events': true,
  'Live events': false,
};

const isPassesFilterOptionMap: Record<string, boolean> = {
  Sent: true,
  'Blocked by filter': false,
};

// Circuit-breaker sentinel status: deliveries skipped while the breaker is
// open are recorded with http_status = -1 and displayed as "Paused" (legacy
// connect-dev-portal app/utils/webhook-statuses.js HTTP_STATUS_PAUSED)
const HTTP_STATUS_PAUSED = -1;

// http_status filter options are displayed as "Paused" for the circuit
// breaker sentinel. This map converts the displayed value back to the
// integer the backend expects (legacy logger-api-call.ts httpStatusOptionMap)
const httpStatusOptionMap: Record<string, number> = {
  Paused: HTTP_STATUS_PAUSED,
};

// Integer-based filters that require special null handling
const intBasedFilterSet = new Set(['http_status']);

// Category field names differ between endpoints
const endpointToCategoryMap: Record<string, string> = {
  api: 'endpoint_category',
  webhook: 'category',
};

/**
 * Creates filter query parameters from a Map of filters
 * Handles special cases for boolean, integer, and string filters
 */
function createFilterQueryParams(filters: Map<string, string[]>): string {
  const params: string[] = [];

  filters.forEach((options, filterName) => {
    if (options && options.length > 0) {
      const processedOptions = options.map((option) => {
        // Boolean filter: is_test
        if (filterName === 'is_test' && option in isTestEventOptionMap) {
          return String(isTestEventOptionMap[option]);
        }

        // Boolean filter: passes_filter
        if (
          filterName === 'passes_filter' &&
          option in isPassesFilterOptionMap
        ) {
          return String(isPassesFilterOptionMap[option]);
        }

        // Display value for the circuit-breaker sentinel encodes back to its
        // integer (http_status "Paused" -> -1)
        if (filterName === 'http_status' && option in httpStatusOptionMap) {
          return String(httpStatusOptionMap[option]);
        }

        // Integer filters: null values represented as -1 or 0
        if (intBasedFilterSet.has(filterName)) {
          return option || '-1,0';
        }

        // String filters: null values represented as ',nil'
        return option ? encodeURIComponent(option) : ',nil';
      });

      params.push(`${filterName}=${processedOptions.join(',')}`);
    }
  });

  return params.join('&');
}

/**
 * Builds query parameters for log event endpoints
 */
function buildLogEventQueryParams(
  params: GetEventsInput,
  filters?: Map<string, string[]>
): URLSearchParams {
  const query = new URLSearchParams();

  // Add standard parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'boolean') {
        query.set(key, value.toString());
      } else if (typeof value === 'number') {
        query.set(key, value.toString());
      } else if (typeof value === 'string') {
        query.set(key, value);
      }
    }
  });

  // Add filter parameters if provided
  if (filters) {
    const filterParams = createFilterQueryParams(filters);
    if (filterParams) {
      // Append filter params directly to maintain proper format
      const filterSearchParams = new URLSearchParams(filterParams);
      filterSearchParams.forEach((value, key) => {
        query.append(key, value);
      });
    }
  }

  return query;
}

export {
  HTTP_STATUS_PAUSED,
  buildLogEventQueryParams,
  createFilterQueryParams,
  endpointToCategoryMap,
  httpStatusOptionMap,
  intBasedFilterSet,
  isPassesFilterOptionMap,
  isTestEventOptionMap,
};
